import type * as React from "react"

const LEAD_STATUSES = {
  NEW: 1,
  CONTACTED: 2,
  QUALIFIED: 3,
  ESTIMATE: 4,
  NEGOTIATION: 5,
  WON: 6,
  LOST: 7,
  UNQUALIFIED: 8,
  ON_HOLD: 9,
  NURTURING: 10,
  BOOKED: 11,
  EXPLORATION: 12,
  CONTRACT: 13,
  DO_NOT_CONTACT: 14,
  // Set by the backend when the project is activated. Distinct from Won: Won
  // is the deal, Converted is the project existing, and only the second one
  // retires the lead from the pipeline into the Archive.
  CONVERTED: 15
} as const

type LeadStatuses = keyof typeof LEAD_STATUSES
type LeadStatusesValues = (typeof LEAD_STATUSES)[LeadStatuses]

const LEAD_STATUSES_MAP: Record<
  LeadStatusesValues,
  {
    id: LeadStatusesValues
    value: LeadStatusesValues
    label: string
  }
> = {
  [LEAD_STATUSES.NEW]: {
    id: LEAD_STATUSES.NEW,
    value: LEAD_STATUSES.NEW,
    label: "New"
  },
  [LEAD_STATUSES.CONTACTED]: {
    id: LEAD_STATUSES.CONTACTED,
    value: LEAD_STATUSES.CONTACTED,
    label: "Contacted"
  },
  [LEAD_STATUSES.QUALIFIED]: {
    id: LEAD_STATUSES.QUALIFIED,
    value: LEAD_STATUSES.QUALIFIED,
    label: "Qualified"
  },
  [LEAD_STATUSES.ESTIMATE]: {
    id: LEAD_STATUSES.ESTIMATE,
    value: LEAD_STATUSES.ESTIMATE,
    label: "Estimate"
  },
  [LEAD_STATUSES.NEGOTIATION]: {
    id: LEAD_STATUSES.NEGOTIATION,
    value: LEAD_STATUSES.NEGOTIATION,
    label: "Negotiation"
  },
  [LEAD_STATUSES.WON]: {
    id: LEAD_STATUSES.WON,
    value: LEAD_STATUSES.WON,
    label: "Won"
  },
  [LEAD_STATUSES.CONVERTED]: {
    id: LEAD_STATUSES.CONVERTED,
    value: LEAD_STATUSES.CONVERTED,
    label: "Converted to Project"
  },
  [LEAD_STATUSES.LOST]: {
    id: LEAD_STATUSES.LOST,
    value: LEAD_STATUSES.LOST,
    label: "Lost"
  },
  [LEAD_STATUSES.UNQUALIFIED]: {
    id: LEAD_STATUSES.UNQUALIFIED,
    value: LEAD_STATUSES.UNQUALIFIED,
    label: "Unqualified"
  },
  [LEAD_STATUSES.ON_HOLD]: {
    id: LEAD_STATUSES.ON_HOLD,
    value: LEAD_STATUSES.ON_HOLD,
    label: "On Hold"
  },
  [LEAD_STATUSES.BOOKED]: {
    id: LEAD_STATUSES.BOOKED,
    value: LEAD_STATUSES.BOOKED,
    label: "Booked"
  },
  [LEAD_STATUSES.EXPLORATION]: {
    id: LEAD_STATUSES.EXPLORATION,
    value: LEAD_STATUSES.EXPLORATION,
    label: "Exploration"
  },
  [LEAD_STATUSES.CONTRACT]: {
    id: LEAD_STATUSES.CONTRACT,
    value: LEAD_STATUSES.CONTRACT,
    label: "Contract"
  },
  [LEAD_STATUSES.NURTURING]: {
    id: LEAD_STATUSES.NURTURING,
    value: LEAD_STATUSES.NURTURING,
    label: "Nurturing"
  },
  [LEAD_STATUSES.DO_NOT_CONTACT]: {
    id: LEAD_STATUSES.DO_NOT_CONTACT,
    value: LEAD_STATUSES.DO_NOT_CONTACT,
    label: "Do Not Contact"
  }
}

/**
 * The client's supplied palette covers six statuses — New, Booked, Exploration,
 * Estimate, Contract and Closed-Lost (this app's Lost). The rest are derived in
 * the same three-token scheme, and are pending the client's sign-off.
 *
 * The lifecycle reads as a hue ramp in stage order: slate → sky → blue → violet
 * → orange → teal → green. Retired stages sit off that ramp, on hues it never
 * uses, so a lead parked on one cannot be mistaken for a live stage.
 */
const leadStatusColorMap: Record<LeadStatusesValues, React.CSSProperties> = {
  [LEAD_STATUSES.NEW]: {
    color: "#64748B",
    backgroundColor: "#F1F5F9",
    borderColor: "#CBD5E1"
  },
  [LEAD_STATUSES.CONTACTED]: {
    color: "#0284C7",
    backgroundColor: "#E6F6FE",
    borderColor: "#A5DFF7"
  },
  [LEAD_STATUSES.BOOKED]: {
    color: "#3B82F6",
    backgroundColor: "#EAF2FF",
    borderColor: "#B7D2FF"
  },
  [LEAD_STATUSES.EXPLORATION]: {
    color: "#6D3CF5",
    backgroundColor: "#F1ECFF",
    borderColor: "#CDBBFF"
  },
  [LEAD_STATUSES.ESTIMATE]: {
    color: "#FF8A3D",
    backgroundColor: "#FFF1E8",
    borderColor: "#FFC99F"
  },
  [LEAD_STATUSES.CONTRACT]: {
    color: "#299B8D",
    backgroundColor: "#E8F7F4",
    borderColor: "#A9DDD6"
  },
  [LEAD_STATUSES.WON]: {
    color: "#16A34A",
    backgroundColor: "#ECF9EF",
    borderColor: "#BFE8C8"
  },
  // Deeper than Won's green: the deal is not just won, it is a live project.
  [LEAD_STATUSES.CONVERTED]: {
    color: "#0F766E",
    backgroundColor: "#E8F7F4",
    borderColor: "#A9DDD6"
  },
  [LEAD_STATUSES.LOST]: {
    color: "#EF4444",
    backgroundColor: "#FFF0F0",
    borderColor: "#FFB8B8"
  },
  [LEAD_STATUSES.QUALIFIED]: {
    color: "#6366F1",
    backgroundColor: "#EEF0FE",
    borderColor: "#C7CCFB"
  },
  [LEAD_STATUSES.NEGOTIATION]: {
    color: "#DB2777",
    backgroundColor: "#FDF0F6",
    borderColor: "#F8C2DD"
  },
  [LEAD_STATUSES.UNQUALIFIED]: {
    color: "#A16207",
    backgroundColor: "#FDF6E3",
    borderColor: "#EBD9A3"
  },
  [LEAD_STATUSES.ON_HOLD]: {
    color: "#F59E0B",
    backgroundColor: "#FFF7E8",
    borderColor: "#FFD79A"
  },
  [LEAD_STATUSES.NURTURING]: {
    color: "#C026D3",
    backgroundColor: "#FDF0FE",
    borderColor: "#F3C2F9"
  },
  // Derived like the overlay pill's slate was — a neutral that reads as
  // "off" — but on stone, since New already owns slate in this palette and
  // a parked lead must not render like a fresh one.
  [LEAD_STATUSES.DO_NOT_CONTACT]: {
    color: "#57534E",
    backgroundColor: "#F5F5F4",
    borderColor: "#D6D3D1"
  }
}

const leadStatusStyles: Record<string, React.CSSProperties> = {
  [LEAD_STATUSES_MAP[LEAD_STATUSES.NEW].label]:
    leadStatusColorMap[LEAD_STATUSES.NEW],
  [LEAD_STATUSES_MAP[LEAD_STATUSES.CONTACTED].label]:
    leadStatusColorMap[LEAD_STATUSES.CONTACTED],
  [LEAD_STATUSES_MAP[LEAD_STATUSES.QUALIFIED].label]:
    leadStatusColorMap[LEAD_STATUSES.QUALIFIED],
  [LEAD_STATUSES_MAP[LEAD_STATUSES.ESTIMATE].label]:
    leadStatusColorMap[LEAD_STATUSES.ESTIMATE],
  [LEAD_STATUSES_MAP[LEAD_STATUSES.NEGOTIATION].label]:
    leadStatusColorMap[LEAD_STATUSES.NEGOTIATION],
  [LEAD_STATUSES_MAP[LEAD_STATUSES.WON].label]:
    leadStatusColorMap[LEAD_STATUSES.WON],
  [LEAD_STATUSES_MAP[LEAD_STATUSES.CONVERTED].label]:
    leadStatusColorMap[LEAD_STATUSES.CONVERTED],
  [LEAD_STATUSES_MAP[LEAD_STATUSES.LOST].label]:
    leadStatusColorMap[LEAD_STATUSES.LOST],
  [LEAD_STATUSES_MAP[LEAD_STATUSES.UNQUALIFIED].label]:
    leadStatusColorMap[LEAD_STATUSES.UNQUALIFIED],
  [LEAD_STATUSES_MAP[LEAD_STATUSES.ON_HOLD].label]:
    leadStatusColorMap[LEAD_STATUSES.ON_HOLD],
  [LEAD_STATUSES_MAP[LEAD_STATUSES.BOOKED].label]:
    leadStatusColorMap[LEAD_STATUSES.BOOKED],
  [LEAD_STATUSES_MAP[LEAD_STATUSES.EXPLORATION].label]:
    leadStatusColorMap[LEAD_STATUSES.EXPLORATION],
  [LEAD_STATUSES_MAP[LEAD_STATUSES.CONTRACT].label]:
    leadStatusColorMap[LEAD_STATUSES.CONTRACT],
  [LEAD_STATUSES_MAP[LEAD_STATUSES.NURTURING].label]:
    leadStatusColorMap[LEAD_STATUSES.NURTURING],
  [LEAD_STATUSES_MAP[LEAD_STATUSES.DO_NOT_CONTACT].label]:
    leadStatusColorMap[LEAD_STATUSES.DO_NOT_CONTACT]
}

/**
 * i18n keys for the status dropdown's hover/focus tooltip (UI/UX guidelines
 * §5.16). Only the seven lifecycle stages are covered here — Archive lead's
 * description lives in `leadsTable.status.descriptions.archive` and is looked
 * up directly where that item is built, since it has no `LeadStatusesValues`
 * of its own.
 */
const leadStatusDescriptionKeys: Partial<Record<LeadStatusesValues, string>> = {
  [LEAD_STATUSES.NEW]: "leadsTable.status.descriptions.new",
  [LEAD_STATUSES.CONTACTED]: "leadsTable.status.descriptions.contacted",
  [LEAD_STATUSES.BOOKED]: "leadsTable.status.descriptions.booked",
  [LEAD_STATUSES.EXPLORATION]: "leadsTable.status.descriptions.exploration",
  [LEAD_STATUSES.ESTIMATE]: "leadsTable.status.descriptions.estimate",
  [LEAD_STATUSES.CONTRACT]: "leadsTable.status.descriptions.contract",
  [LEAD_STATUSES.WON]: "leadsTable.status.descriptions.won"
}

export {
  LEAD_STATUSES_MAP,
  LEAD_STATUSES,
  leadStatusStyles,
  leadStatusColorMap,
  leadStatusDescriptionKeys,
  type LeadStatusesValues
}
